import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {ExpressionDryRunPill} from '../../../editors/expressions/expressionDryRunPill';
import {getExpressionTypeIcon} from '../../../editors/expressions/expressionIcons';
import {ExpressionDryRunSnapshot} from '../../../editors/expressions/expressionModel';
import {InferredType} from '../../../editors/expressions/infer/expressionResultModel';

/*
 * Props.
 */

export interface ExpressionPillProps {
  label: ReactNode;
  type: InferredType;
  dryRun?: ExpressionDryRunSnapshot;
  isInvalid?: boolean;
  disableHover?: boolean;
  paletteColor?: PaletteColorsEnum;

  className?: string;
}

/*
 * Styles.
 */

const StyledExpressionWrapperDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledExpressionDiv = styled.div<{
  $isInvalid: boolean;
  $disableHover: boolean;
  $paletteColor: PaletteColorsEnum;
}>`
  display: flex;

  width: min-content;
  max-width: 100%;
  min-width: 0;

  padding: 3px 6px;

  height: 22px;
  line-height: 16px;

  background-color: ${(p) => p.theme.palette[p.$paletteColor].shade20};
  color: ${(p) => p.theme.palette[p.$paletteColor].shade50};
  border-radius: 4px;

  ${(p) =>
    !p.$disableHover &&
    css`
      &:hover {
        background-color: ${p.theme.palette[p.$paletteColor].shade30};
      }
    `}

  ${(p) =>
    p.$isInvalid &&
    css`
      background-color: ${p.theme.palette.red.shade20};
      color: ${p.theme.palette.red.shade50};

      ${!p.$disableHover &&
      css`
        &:hover {
          background-color: ${p.theme.palette.red.shade30};
        }
      `}
    `}

  & > :not(:last-child) {
    margin-right: 4px;
  }
`;

const StyledExpressionText = styled.div`
  ${ellipsis()};
  font-weight: ${fontWeights.semibold};
`;

/*
 * Component.
 */

export const ExpressionPill: FC<ExpressionPillProps> = ({
  label,
  type,
  dryRun,
  paletteColor = PaletteColorsEnum.TEAL,
  isInvalid = false,
  disableHover = false,
  className,
}) => {
  const theme = useUserTheme();

  return (
    <StyledExpressionWrapperDiv>
      <StyledExpressionDiv
        className={className}
        $isInvalid={isInvalid || !label}
        $disableHover={disableHover}
        $paletteColor={paletteColor}
      >
        <Icon name={getExpressionTypeIcon(type).small.filled} />
        <StyledExpressionText>
          {label ?? <Trans id="rule-editor-unknown-expression">[Unknown expression]</Trans>}
        </StyledExpressionText>
        {(isInvalid || !label) && <Icon name="warningFilledSmall" color={theme.palette.red.shade50} />}
      </StyledExpressionDiv>
      {dryRun && <ExpressionDryRunPill dryRun={dryRun} title={label} />}
    </StyledExpressionWrapperDiv>
  );
};
